import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Grid, IconButton, MenuItem, Select, Stack, Tooltip, Typography, Button } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronRight as ChevronRightIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
} from '@mui/icons-material';
import { getFileIcon } from 'assets/icons/fileTypes/fileIconMap';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Spinner, TwoOptionsDialog } from 'components';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { useApiRequests } from 'hooks/useApiRequests';
import { Info } from 'components/Info';
import { DocumentWithMetadataDto } from 'types/document';
import NoDocumentsOverlay from '../NoDocumentsOverlay/NoDocumentsOverlay';
import { MoreOptions } from '../MoreOptions/MoreOptions';
import { useDialog } from 'hooks';
import RenameFileDialog from 'components/Dialogs/RenameFileDialog/RenameFileDialog';
import FileIdDialog from 'components/Dialogs/FileIdDialog/FileIdDialog';
import { getIntlLocale } from 'utils';

import styles from './MyDocuments.module.scss';

const rowsPerPageOptions = [5, 10, 25];

export function MyDocuments() {
  const { t, i18n } = useTranslation('settings');
  const isRtl = i18n.dir() === 'rtl';
  const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');
  const {
    listPersonalDocumentsWithMetadata,
    uploadPersonalDocument,
    downloadPersonalDocument,
    updatePinnedState,
    renamePersonalDocument,
    deletePersonalDocument,
    getBlobPath,
  } = useApiRequests();
  const user = useAppSelector(selectUser);
  const tableRef = useRef<HTMLDivElement>(null);
  const { showDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const [sortConfig, setSortConfig] = useState<{
    column: 'name' | 'date' | 'size' | null;
    order: 'asc' | 'desc' | 'none';
  }>({ column: 'date', order: 'desc' }); // default is Z-A date

  const [documents, setDocuments] = useState<DocumentWithMetadataDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);
  const [usedStorageMb, setUsedStorageMb] = useState<number>(0);
  const [selectedFilePaths, setSelectedFilePaths] = useState<Set<string>>(new Set());

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handleSort = (column: 'name' | 'date' | 'size') => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        const nextOrder = prev.order === 'none' ? 'asc' : prev.order === 'asc' ? 'desc' : 'none';
        return { column, order: nextOrder };
      }
      return { column, order: 'asc' };
    });
  };

  const sortDocuments = (
    list: DocumentWithMetadataDto[],
    sortBy: 'name' | 'date' | 'size',
    sortOrder: 'asc' | 'desc' | 'none',
  ): DocumentWithMetadataDto[] => {
    const sortFn = (a: DocumentWithMetadataDto, b: DocumentWithMetadataDto) => {
      if (sortOrder === 'none') return 0;

      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortBy) {
        case 'name':
          aVal = a.path;
          bVal = b.path;
          break;
        case 'date':
          aVal = new Date(a.metadata?.uploadedAt || '').getTime();
          bVal = new Date(b.metadata?.uploadedAt || '').getTime();
          break;
        case 'size':
          aVal = a.metadata?.size || 0;
          bVal = b.metadata?.size || 0;
          break;
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else {
        return sortOrder === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      }
    };

    const pinnedDocs = list.filter((d) => d.metadata?.pinned).sort(sortFn);
    const unpinnedDocs = list.filter((d) => !d.metadata?.pinned).sort(sortFn);

    return [...pinnedDocs, ...unpinnedDocs];
  };

  const sortedDocuments = useMemo(() => {
    const pinnedDocs = documents.filter((doc) => doc.metadata?.pinned);
    const unpinnedDocs = documents.filter((doc) => !doc.metadata?.pinned);

    if (!sortConfig.column || sortConfig.order === 'none') {
      return [...pinnedDocs, ...unpinnedDocs];
    }

    const sortedUnpinned = sortDocuments(unpinnedDocs, sortConfig.column, sortConfig.order);
    return [...pinnedDocs, ...sortedUnpinned];
  }, [documents, sortConfig]);

  const pagedDocuments = sortedDocuments.slice(startIndex, endIndex);

  const paginationText = `${startIndex + 1}-${Math.min(endIndex, documents.length)} ${t('documentsCommon.of')} ${
    documents.length
  }`;

  useEffect(() => {
    setSelectedFilePaths((prev) => {
      const validPaths = new Set(documents.map((doc) => doc.path));
      const next = new Set(Array.from(prev).filter((path) => validPaths.has(path)));
      return next.size === prev.size ? prev : next;
    });
  }, [documents]);

  const handleDownload = async (fileName: string) => {
    if (!user?.clinicId || !user?.id) return;
    const path = `Personal/${user.clinicId}/${user.id}`;
    try {
      await downloadPersonalDocument(path, fileName);
    } catch {
      enqueueSnackbar(t('documentsCommon.downloadFailed'), { variant: 'error' });
    }
  };

  const handleRename = (fileName: string) => {
    const modal: { destroy: () => void } = showDialog(RenameFileDialog, {
      defaultFileName: fileName,
      onCancel: () => modal.destroy(),
      onConfirm: async (newName: string) => {
        modal.destroy();
        if (!user?.clinicId || !user?.id || !newName || newName === fileName) return;

        const path = `Personal/${user.clinicId}/${user.id}`;
        try {
          await renamePersonalDocument(path, fileName, newName);
          const list = await listPersonalDocumentsWithMetadata(path);
          const sorted = [...list].sort((a, b) => {
            const aPinned = a.metadata?.pinned === true;
            const bPinned = b.metadata?.pinned === true;
            return aPinned === bPinned ? 0 : aPinned ? -1 : 1;
          });
          setDocuments(sorted);
          setTotalDocuments(sorted.length);
          const totalBytes = sorted.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
          setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
          enqueueSnackbar(t('documentsCommon.fileRenamedSuccessfully'), { variant: 'success' });
        } catch {
          enqueueSnackbar(t('documentsCommon.renameFailed'), { variant: 'error' });
        }
      },
    });
  };

  const handleFileId = async (fileName: string) => {
    if (!user?.clinicId || !user?.id) return;

    try {
      const { id } = await getBlobPath(user.clinicId, user.id, fileName);

      const modal: { destroy: () => void } = showDialog(FileIdDialog, {
        fileId: id,
        onConfirm: (copiedId: string) => {
          modal.destroy();
          navigator.clipboard.writeText(copiedId);
          enqueueSnackbar(t('documentsCommon.fileIdCopiedToClipboard'), { variant: 'success' });
        },
        onCancel: () => modal.destroy(),
      });
    } catch {
      enqueueSnackbar(t('documentsCommon.failedToRetrieveFileId'), { variant: 'error' });
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!user?.clinicId || !user?.id) return;

    const modal = showDialog(TwoOptionsDialog, {
      title: t('documentsCommon.deleteFileUpper'),
      text: (
        <Typography variant='body2' component='p'>
          {t('documentsCommon.deleteFileQuestion', { fileName })}
        </Typography>
      ),
      confirmText: t('documentsCommon.delete'),
      onConfirm: async () => {
        modal.destroy();

        const path = `Personal/${user.clinicId}/${user.id}`;
        try {
          await deletePersonalDocument(path, fileName);
          const list = await listPersonalDocumentsWithMetadata(path);
          const sorted = [...list].sort((a, b) => {
            const aPinned = a.metadata?.pinned === true;
            const bPinned = b.metadata?.pinned === true;
            return aPinned === bPinned ? 0 : aPinned ? -1 : 1;
          });
          setDocuments(sorted);
          setTotalDocuments(sorted.length);
          const totalBytes = sorted.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
          setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
          enqueueSnackbar(t('documentsCommon.fileDeletedSuccessfully'), { variant: 'success' });
        } catch {
          enqueueSnackbar(t('documentsCommon.deleteFailed'), { variant: 'error' });
        }
      },
    });
  };

  const handleRowSelection = (docPath: string) => {
    setSelectedFilePaths((prev) => {
      const next = new Set(prev);
      if (next.has(docPath)) {
        next.delete(docPath);
      } else {
        next.add(docPath);
      }
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (!user?.clinicId || !user?.id || selectedFilePaths.size === 0) return;

    const path = `Personal/${user.clinicId}/${user.id}`;
    const selectedPathsArray = Array.from(selectedFilePaths);
    const selectedNames = selectedPathsArray
      .map((docPath) => docPath.split('/').pop())
      .filter((name): name is string => Boolean(name));

    if (selectedNames.length === 0) return;

    const modal = showDialog(TwoOptionsDialog, {
      title: t('documentsCommon.deleteFilesUpper'),
      text: (
        <Typography variant='body2' component='p'>
          {t('documentsCommon.deleteFilesQuestion')}
        </Typography>
      ),
      confirmText: t('documentsCommon.delete'),
      onConfirm: async () => {
        modal.destroy();
        setLoading(true);

        try {
          const results = await Promise.allSettled(
            selectedNames.map((fileName) => deletePersonalDocument(path, fileName)),
          );
          const failedCount = results.filter((result) => result.status === 'rejected').length;
          const deletedCount = results.length - failedCount;

          const list = await listPersonalDocumentsWithMetadata(path);
          const sorted = [...list].sort((a, b) => {
            const aPinned = a.metadata?.pinned === true;
            const bPinned = b.metadata?.pinned === true;
            return aPinned === bPinned ? 0 : aPinned ? -1 : 1;
          });
          setDocuments(sorted);
          setTotalDocuments(sorted.length);
          const totalBytes = sorted.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
          setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
          setSelectedFilePaths(new Set());

          if (failedCount === 0) {
            enqueueSnackbar(t('documentsCommon.filesDeletedSuccessfully', { count: deletedCount }), {
              variant: 'success',
            });
          } else {
            enqueueSnackbar(t('documentsCommon.filesFailedToDelete', { count: failedCount }), {
              variant: 'error',
            });
          }
        } catch {
          enqueueSnackbar(t('documentsCommon.deleteFailed'), { variant: 'error' });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleMultipleFileUpload = async (files: File[]) => {
    if (!files.length || !user?.clinicId || !user?.id) return;

    const path = `Personal/${user.clinicId}/${user.id}`;
    setLoading(true);

    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          enqueueSnackbar(t('documentsCommon.fileTooLarge', { fileName: file.name }), { variant: 'error' });
          continue;
        }

        const alreadyExists = documents.some((doc) => doc.path.endsWith(file.name));
        const confirmOverwrite = alreadyExists
          ? window.confirm(t('documentsCommon.overwriteFileQuestion', { fileName: file.name }))
          : true;
        if (!confirmOverwrite) continue;

        try {
          await uploadPersonalDocument(file, path, true);
          enqueueSnackbar(t('documentsCommon.uploadedFile', { fileName: file.name }), { variant: 'success' });
        } catch (err) {
          enqueueSnackbar(t('documentsCommon.failedToUploadFile', { fileName: file.name }), { variant: 'error' });
        }
      }

      const list = await listPersonalDocumentsWithMetadata(path);
      const sortedList = sortDocuments(list, sortConfig.column ?? 'date', sortConfig.order);
      setDocuments(sortedList);
      setTotalDocuments(list.length);
      const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
      setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
    } catch (err) {
      enqueueSnackbar(t('documentsCommon.uploadError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePinned = async (filename: string) => {
    const path = `Personal/${user?.clinicId}/${user?.id}`;
    const current = documents.find((doc) => doc.path.endsWith(filename));
    const wasPinned = current?.metadata?.pinned === true;

    try {
      await updatePinnedState(path, filename, !wasPinned);
      const list = await listPersonalDocumentsWithMetadata(path);

      // Sort so pinned items are at the top
      const sortedList = [...list].sort((a, b) => {
        const aPinned = a.metadata?.pinned === true;
        const bPinned = b.metadata?.pinned === true;
        return aPinned === bPinned ? 0 : aPinned ? -1 : 1;
      });

      setDocuments(sortedList);
      setTotalDocuments(sortedList.length);

      const totalBytes = sortedList.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
      setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));

      enqueueSnackbar(
        wasPinned
          ? t('documentsCommon.unpinnedFile', { fileName: filename })
          : t('documentsCommon.pinnedFile', { fileName: filename }),
        { variant: 'success' },
      );
    } catch {
      enqueueSnackbar(t('documentsCommon.failedToUpdatePinnedState'), { variant: 'error' });
    }
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<string>) => {
    const newValue = parseInt(event.target.value, 10);
    setRowsPerPage(newValue);
    setCurrentPage(1);
  };

  const handleFirstPageClick = () => setCurrentPage(1);
  const handlePreviousPageClick = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPageClick = () => setCurrentPage((prev) => (endIndex < documents.length ? prev + 1 : prev));
  const handleLastPageClick = () => setCurrentPage(Math.ceil(documents.length / rowsPerPage));

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user?.clinicId || !user?.id) return;

      const path = `Personal/${user.clinicId}/${user.id}`;

      try {
        setLoading(true);
        const list = await listPersonalDocumentsWithMetadata(path);

        // ✅ Sort so pinned files appear at the top
        const sortedList = [...list].sort((a, b) => {
          const aPinned = String(a.metadata?.pinned) === 'true';
          const bPinned = String(b.metadata?.pinned) === 'true';
          return aPinned === bPinned ? 0 : aPinned ? -1 : 1;
        });

        setDocuments(sortedList);
        setTotalDocuments(sortedList.length);

        const totalBytes = sortedList.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
        setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
      } catch {
        enqueueSnackbar(t('documentsCommon.failedToLoadDocuments'), { variant: 'error' });
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchDocuments();
  }, [user?.clinicId, user?.id]);

  const getArrow = (column: string) => {
    if (sortConfig.column !== column) return '';
    return sortConfig.order === 'asc' ? '▲' : sortConfig.order === 'desc' ? '▼' : '';
  };

  return (
    <div className={`${styles.container} ${loading && isInitialLoad ? styles.loadingInitial : ''}`}>
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        onChange={async (e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length === 0 || !user?.clinicId || !user?.id) return;

          setLoading(true); // Show spinner

          const path = `Personal/${user.clinicId}/${user.id}`;

          try {
            for (const file of files) {
              if (file.size > 25 * 1024 * 1024) {
                enqueueSnackbar(t('documentsCommon.fileTooLarge', { fileName: file.name }), { variant: 'error' });
                continue;
              }

              setLoading(true);

              const filename = file.name;
              const alreadyExists = documents.some((doc) => doc.path.endsWith(filename));
              const confirmOverwrite = alreadyExists
                ? window.confirm(t('documentsCommon.overwriteFileQuestion', { fileName: filename }))
                : true;

              if (!confirmOverwrite) continue;

              try {
                await uploadPersonalDocument(file, path, true);
                enqueueSnackbar(t('documentsCommon.uploadedFile', { fileName: filename }), { variant: 'success' });
              } catch (err) {
                enqueueSnackbar(t('documentsCommon.failedToUploadFile', { fileName: filename }), { variant: 'error' });
              }
            }

            // Refresh document list after all uploads
            const list = await listPersonalDocumentsWithMetadata(path);
            const sortedList = sortDocuments(list, sortConfig.column ?? 'date', sortConfig.order);
            setDocuments(sortedList);
            setTotalDocuments(list.length);
            const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
            setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
          } catch (err) {
            enqueueSnackbar(t('documentsCommon.uploadError'), { variant: 'error' });
          } finally {
            setLoading(false); // Hide spinner
            (e.target as HTMLInputElement).value = ''; // Reset input
          }
        }}
      />

      <>
        {loading && (
          <div className={styles.overlay}>
            <Spinner small transparent />
          </div>
        )}

        {isInitialLoad ? null : documents.length === 0 ? (
          <NoDocumentsOverlay onFileSelect={handleMultipleFileUpload} />
        ) : (
          <div className={styles.mainWrapper}>
            <div
              className={`${styles.dropZone} ${isDragging ? styles.dragActive : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={async (e) => {
                e.preventDefault();
                setIsDragging(false);
                const files = Array.from(e.dataTransfer.files ?? []);
                if (!files.length || !user?.clinicId || !user?.id) return;

                setLoading(true);
                const path = `Personal/${user.clinicId}/${user.id}`;

                try {
                  for (const file of files) {
                    if (file.size > 25 * 1024 * 1024) {
                      enqueueSnackbar(t('documentsCommon.fileTooLarge', { fileName: file.name }), { variant: 'error' });
                      continue;
                    }

                    setLoading(true);

                    const filename = file.name;
                    const alreadyExists = documents.some((doc) => doc.path.endsWith(filename));
                    const confirmOverwrite = alreadyExists
                      ? window.confirm(t('documentsCommon.overwriteFileQuestion', { fileName: filename }))
                      : true;

                    if (!confirmOverwrite) continue;

                    try {
                      await uploadPersonalDocument(file, path, true);
                      enqueueSnackbar(t('documentsCommon.uploadedFile', { fileName: filename }), {
                        variant: 'success',
                      });
                    } catch (err) {
                      enqueueSnackbar(t('documentsCommon.failedToUploadFile', { fileName: filename }), {
                        variant: 'error',
                      });
                    }
                  }

                  const list = await listPersonalDocumentsWithMetadata(path);
                  const sortedList = sortDocuments(list, sortConfig.column ?? 'date', sortConfig.order);
                  setDocuments(sortedList);
                  setTotalDocuments(list.length);
                  const totalBytes = list.reduce((sum, d) => sum + (d?.metadata?.size ?? 0), 0);
                  setUsedStorageMb(Number((totalBytes / 1024 / 1024).toFixed(1)));
                } catch (err) {
                  enqueueSnackbar(t('documentsCommon.uploadError'), { variant: 'error' });
                } finally {
                  setLoading(false);
                }
              }}
            >
              <div className={styles.main}>
                {/* New Header */}
                <Stack className={styles.header} direction='row' justifyContent='space-between' alignItems='center'>
                  <Stack spacing={1}>
                    <Typography variant='body2'>
                      <b>{totalDocuments}</b> {t('myDocuments.totalDocuments')}
                    </Typography>
                    <Stack direction='row' alignItems='center' className={styles.storageInfoRow}>
                      <Typography variant='body2'>
                        {isRtl ? (
                          <>
                            {t('myDocuments.storageRemaining')}{' '}
                            <span className={styles.storageValue}>
                              <b>{usedStorageMb} MB</b> / <b>500 MB</b>
                            </span>
                          </>
                        ) : (
                          <>
                            <span className={styles.storageValue}>
                              <b>{usedStorageMb} MB</b> / <b>500 MB</b>
                            </span>{' '}
                            {t('myDocuments.storageRemaining')}
                          </>
                        )}
                      </Typography>
                      <Info text={t('myDocuments.manageFilesInfo')} />
                    </Stack>
                  </Stack>

                  <Stack direction='row' spacing={1} alignItems='center'>
                    {selectedFilePaths.size > 0 && (
                      <Button variant='contained' color='error' sx={{ color: '#fff' }} onClick={handleDeleteSelected}>
                        {t('documentsCommon.delete')}
                      </Button>
                    )}

                    {selectedFilePaths.size === 0 && (
                      <Button
                        variant='contained'
                        className={styles.inviteButton}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {t('documentsCommon.uploadFile')}
                      </Button>
                    )}
                  </Stack>
                </Stack>

                <div className={styles.table} ref={tableRef}>
                  <Grid className={styles.tableHeader}>
                    <Typography variant='subtitle2' component='span' />
                    <Typography variant='subtitle2' component='span'>
                      {t('documentsCommon.type')} {getArrow?.('type')}
                    </Typography>
                    <Typography
                      variant='subtitle2'
                      component='span'
                      className={styles.sortableHeader}
                      onClick={() => handleSort('name')}
                    >
                      {t('documentsCommon.fileName')} {getArrow?.('name')}
                    </Typography>
                    <Typography
                      variant='subtitle2'
                      component='span'
                      className={`${styles.dateCell} ${styles.sortableHeader}`}
                      onClick={() => handleSort('date')}
                    >
                      {t('documentsCommon.date')} {getArrow?.('date')}
                    </Typography>
                    <Typography
                      variant='subtitle2'
                      component='span'
                      className={`${styles.sizeCell} ${styles.sortableHeader}`}
                      onClick={() => handleSort('size')}
                    >
                      {t('documentsCommon.size')} {getArrow?.('size')}
                    </Typography>
                    <Typography variant='subtitle2' component='span' />
                  </Grid>

                  <Box
                    sx={{
                      maxHeight: rowsPerPage > 10 ? 'calc(47px * 10)' : 'unset',
                      overflowY: rowsPerPage > 10 ? 'auto' : 'unset',
                      scrollbarGutter: 'stable',
                      pr: 1,
                    }}
                  >
                    {pagedDocuments.map((doc) => {
                      if (!doc?.path || typeof doc.path !== 'string') return null;

                      const fileName = doc.path.split('/').pop() ?? '';
                      const extension = fileName.split('.').pop()?.toLowerCase() || '';
                      const FileTypeIcon = getFileIcon(extension);
                      const pinned = doc.metadata?.pinned ?? false;
                      const uploadedAt = doc.metadata?.uploadedAt ?? null;
                      const size = doc.metadata?.size ?? null;

                      return (
                        <Grid
                          key={doc.path}
                          className={`${styles.tableItem} ${selectedFilePaths.has(doc.path) ? styles.selected : ''}`}
                          onClick={() => handleRowSelection(doc.path)}
                        >
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleTogglePinned(fileName);
                            }}
                            className={`${styles.starButton} ${styles.pin}`}
                          >
                            {pinned ? (
                              <StarIcon sx={{ width: 20, height: 20, color: '#FFC107' }} />
                            ) : (
                              <StarBorderIcon sx={{ width: 20, height: 20, color: 'var(--app-text-muted)' }} />
                            )}
                          </IconButton>

                          <Tooltip title={extension.toUpperCase()} className={styles.type}>
                            <span>
                              <FileTypeIcon sx={{ fontSize: 24 }} />
                            </span>
                          </Tooltip>

                          <Tooltip title={fileName}>
                            <Typography variant='body1' className={styles.fileName} noWrap>
                              {fileName}
                            </Typography>
                          </Tooltip>

                          <Typography
                            variant='body1'
                            className={styles.dateCell}
                            data-label={t('documentsCommon.date')}
                          >
                            {uploadedAt ? new Date(uploadedAt).toLocaleDateString(locale) : '—'}
                          </Typography>

                          <Typography
                            variant='body1'
                            className={styles.sizeCell}
                            data-label={t('documentsCommon.size')}
                          >
                            <span className={styles.sizeValue}>
                              {size
                                ? size >= 1024 * 1024
                                  ? `${(size / 1024 / 1024).toFixed(1)} MB`
                                  : `${(size / 1024).toFixed(1)} KB`
                                : '—'}
                            </span>
                          </Typography>

                          <div onClick={(event) => event.stopPropagation()}>
                            <MoreOptions
                              item={doc}
                              onDownload={() => handleDownload(fileName)}
                              onRename={() => handleRename(fileName)}
                              onFileId={() => handleFileId(fileName)}
                              onDelete={() => handleDelete(fileName)}
                              className={styles.actions}
                            />
                          </div>
                        </Grid>
                      );
                    })}
                  </Box>
                </div>
              </div>

              <Stack className={styles.paginationRow}>
                <div className={styles.topRow}>
                  <Stack direction='row' alignItems='center' spacing={1} className={styles.rowsPerPage}>
                    <Typography variant='body1'>{t('documentsCommon.rowsPerPage')}</Typography>
                    <Select
                      className={styles.select}
                      variant='outlined'
                      value={rowsPerPage.toString()}
                      onChange={handleRowsPerPageChange}
                      IconComponent={ChevronRightIcon}
                    >
                      {rowsPerPageOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </div>

                <Stack direction='row' alignItems='center' className={styles.arrowRow}>
                  <Typography variant='body1' className={styles.paginationText}>
                    {paginationText}
                  </Typography>
                  <IconButton onClick={handleFirstPageClick} className={styles.paginationIcon}>
                    <FirstPageIcon />
                  </IconButton>
                  <IconButton onClick={handlePreviousPageClick} className={styles.paginationIcon}>
                    <ArrowBackIcon />
                  </IconButton>
                  <IconButton onClick={handleNextPageClick} className={styles.paginationIcon}>
                    <ArrowForwardIcon />
                  </IconButton>
                  <IconButton onClick={handleLastPageClick} className={styles.paginationIcon}>
                    <LastPageIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </div>
          </div>
        )}
      </>
    </div>
  );
}

export default MyDocuments;
